import { SubsidiaryService } from './../../../../core/services/subsidiary.service';
import { Permissions } from './../../../../core/constants/permissions';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import {
  BalanceThresholds,
  CaxtonEnabledRequest,
  CreateAndLinkSubsidiaryRequest,
  SetTrialBalanceAlertsRequest,
  TrialPaymentDetailsViewModel
} from '../trial-payments.model';
import { TrialService } from "../../../../core/services/trial.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { AuthService } from "../../../../core/services/auth.service";
import { ModalComponent } from "../../../../shared/modal/modal.component";
import { PaymentService } from "../../../../core/services/payment.service";
import { LogHelper } from '../../../../core/helpers/log.helper';
import { forbiddenEmailValidator } from '../../../../core/validators/forbidden-email.validator';
import { SelectOption } from '../../../../core/models/select-option.model';
import { requiredNoWhitespaceValidator } from "../../../../core/validators/no-whitespace.validator";

@Component({
  selector: 'app-trial-payments',
  templateUrl: './trial-payments.component.html',
  styleUrls: ['./trial-payments.component.scss']
})
export class TrialPaymentsComponent implements OnInit {
  @ViewChild('alertsModal') alertsModal: ModalComponent;
  @ViewChild('createSubsidiaryModal') createSubsidiaryModal: ModalComponent;

  @Input() trialId: string;
  @Input() virtualCardInAppEnabled: boolean;
  @Input() nmibleBankInAppEnabled: boolean;

  trialPaymentDetailsViewModel: TrialPaymentDetailsViewModel;
  dataLoaded = false;
  textConnectionIsProcessing = false;
  balanceThresholds: BalanceThresholds[];
  alertForm: UntypedFormGroup;
  createSubsidiaryForm: UntypedFormGroup;
  alertUpdateProcessing: boolean = false;
  saveProcessing: boolean = false;
  Permissions = Permissions;
  createSubsidiaryProcessing: boolean = false;
  linkActions: SelectOption[] = [
    new SelectOption('New', 'Create new subsidiary'),
    new SelectOption('Existing', 'Link to existing subsidiary'),
  ];
  availableExistingSubsidiaries: SelectOption[];
  filteredExistingSubsidiaries: SelectOption[];

  constructor(
    private readonly trialService: TrialService,
    private readonly alertService: AlertService,
    private readonly paymentService: PaymentService,
    private cd: ChangeDetectorRef,
    public authService: AuthService,
    private readonly subsidiaryService: SubsidiaryService) { }

  ngOnInit(): void {
    this.alertForm = new UntypedFormGroup({
      alertEmail: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.email])
    });

    this.initiateCreateSubsidiaryForm();

    this.trialService.getTrialPaymentsInfo(this.trialId).subscribe({
      next: response => {
        this.trialPaymentDetailsViewModel = response;
        this.dataLoaded = true;
        if (this.trialPaymentDetailsViewModel.caxtonDataAvailable && this.trialPaymentDetailsViewModel.caxtonAccountDetails == undefined) {
          this.alertService.showWarningAlert('Unable to retrieve trial payments info');
        }
      },
      error: () => {
        this.alertService.showWarningAlert('Unable to retrieve trial payments info');
        this.dataLoaded = true;
      }
    });

    this.createSubsidiaryForm.get('linkAction').valueChanges.subscribe(value => {
      this.onLinkActionChanged(value);
    });
  }

  testConnectionToCaxton() {
    if (!this.trialPaymentDetailsViewModel.caxtonDataAvailable) {
      this.createSubsidiaryForm.get("subsidiaryName").setValue(this.trialPaymentDetailsViewModel.trialLabel);
      this.createSubsidiaryForm.get("ownerEmail").setValue(this.trialPaymentDetailsViewModel.coordinatorEmail);
      this.createSubsidiaryModal.show();
      return;
    }

    this.textConnectionIsProcessing = true;
    this.paymentService.getBalances(this.trialId).subscribe({
      next: balances => {
        this.alertService.showSuccessAlert('Connection successful');
        this.trialPaymentDetailsViewModel.caxtonBalances = balances;
        this.textConnectionIsProcessing = false;
      },
      error: () => {
        this.alertService.showWarningAlert('Connection to Caxton failed');
        this.textConnectionIsProcessing = false;
      }
    });
  }

  getBalanceAlerts() {
    this.alertsModal.show();
    this.trialService.getBalanceAlerts(this.trialId).subscribe(result => {
      this.balanceThresholds = result.balanceThresholds;
      this.alertForm.controls.alertEmail.setValue(result.alertEmail);
      result.balanceThresholds.forEach(t => {
        this.alertForm.addControl(t.currency, new UntypedFormControl(t.balance, Validators.min(0)));
      });
    });
  }

  updateAlerts() {
    this.alertUpdateProcessing = true;
    const request: SetTrialBalanceAlertsRequest = {
      trialId: this.trialId,
      alertEmail: this.alertForm.get('alertEmail').value,
      currencyThresholds: []
    };

    Object.keys(this.alertForm.controls).forEach(key => {
      if (key !== 'alertEmail') {
        request.currencyThresholds.push({
          currency: key,
          balance: this.alertForm.get(key).value
        });
      }
    });

    this.trialService.updateBalanceAlerts(request).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Alerts successfully set.');
        this.alertsModal.hide();
        this.alertUpdateProcessing = false;
      },
      error: () => {
        this.alertService.showWarningAlert('Failed to set alerts. Please try again or contact support');
        this.alertUpdateProcessing = false;
      }
    });
  }

  caxtonSwitch() {
    this.trialPaymentDetailsViewModel.caxtonBankEnabled = !this.trialPaymentDetailsViewModel.caxtonBankEnabled;

    if (!this.trialPaymentDetailsViewModel.caxtonBankEnabled) {
      this.trialPaymentDetailsViewModel.automaticFundsTransferEnabled = false;
    }
  }

  automaticFundsSwitch() {
    this.trialPaymentDetailsViewModel.automaticFundsTransferEnabled = !this.trialPaymentDetailsViewModel.automaticFundsTransferEnabled;
  }

  nmibleVirtualCardsSwitch() {
    this.trialPaymentDetailsViewModel.nmibleEnabled = !this.trialPaymentDetailsViewModel.nmibleEnabled;
  }

  setCaxtonEnabled() {
    this.saveProcessing = true;

    const request: CaxtonEnabledRequest = {
      trialId: this.trialId,
      caxtonEnabled: this.trialPaymentDetailsViewModel.caxtonBankEnabled,
      automaticFundsTransferEnabled: this.trialPaymentDetailsViewModel.automaticFundsTransferEnabled,
      nmibleEnabled: this.trialPaymentDetailsViewModel.nmibleEnabled
    };

    this.trialService.setCaxtonEnabled(request).subscribe({
      next: () => {
        this.saveProcessing = false;
        this.alertService.showSuccessAlert('Changes saved successfully.');
      },
      error: error => {
        this.saveProcessing = false;
        this.alertService.showWarningAlert(error.error ? error.error.title : 'Failed to save changes. Please try again.');
      }
    });
  }

  initiateCreateSubsidiaryForm() {
    this.createSubsidiaryForm = new UntypedFormGroup({
      linkAction: new UntypedFormControl('New'),
      subsidiaryName: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.minLength(5), Validators.maxLength(50)]),
      ownerEmail: new UntypedFormControl('', [requiredNoWhitespaceValidator(), Validators.email, forbiddenEmailValidator(['support@patientgo.com', 'support@illingworthresearch.com'])]),
      subsidiaryId: new UntypedFormControl('')
    });

    this.subsidiaryService.list().subscribe({
      next: subsidiaries => {
        this.availableExistingSubsidiaries = subsidiaries
          .sort((a, b) => {
            if (a.name > b.name)
              return 1;

            if (a.name < b.name)
              return -1;

            return 0;
          })
          .map(subsidiary => new SelectOption(subsidiary.id, subsidiary.name));
        this.filteredExistingSubsidiaries = this.availableExistingSubsidiaries;
      },
      error: error => {
        LogHelper.log(error);
        this.alertService.showWarningAlert('Unable to load existing subsidiaries');
      }
    })
  }

  onConfirmCreateSubsidiary() {
    this.createSubsidiaryProcessing = true;
    let request = this.createSubsidiaryForm.value as CreateAndLinkSubsidiaryRequest;
    request.trialId = this.trialId;

    this.subsidiaryService.createAndLinkSubsidiary(request).subscribe({
      next: response => {
        this.createSubsidiaryProcessing = false;

        this.trialPaymentDetailsViewModel.caxtonDataAvailable = response.caxtonDataAvailable;
        this.trialPaymentDetailsViewModel.caxtonBalances = response.caxtonBalances;
        this.trialPaymentDetailsViewModel.caxtonAccountDetails = response.caxtonAccountDetails;

        this.createSubsidiaryModal.hide();
        this.createSubsidiaryForm.get('linkAction').setValue('New');

        this.alertService.showSuccessAlert("Caxton Subsidiary Successfully Linked");
      },
      error: error => {
        this.createSubsidiaryProcessing = false;
        this.alertService.showWarningAlert(error.error ? error.error.title : "Failed to create and link Caxton Subsidiary. Please try again or contact support.");
        LogHelper.log(error);
      }
    });
  }

  onSubsidiariesFiltered(value) {
    if (value) {
      this.filteredExistingSubsidiaries = this.availableExistingSubsidiaries.filter(option => {
        return (option.text || '').toLowerCase().includes(value.toLowerCase());
      });
    } else {
      this.filteredExistingSubsidiaries = this.availableExistingSubsidiaries;
    }
  }

  onLinkActionChanged(linkAction: string) {
    // update validators
    if (linkAction === 'New') {
      this.createSubsidiaryForm.get('subsidiaryName').setValidators([Validators.required, Validators.minLength(5), Validators.maxLength(50)]);
      this.createSubsidiaryForm.get('ownerEmail').setValidators([Validators.required, Validators.email, forbiddenEmailValidator(['support@patientgo.com', 'support@illingworthresearch.com'])]);
      this.createSubsidiaryForm.get('subsidiaryId').clearValidators();
    } else {
      this.createSubsidiaryForm.get('subsidiaryName').clearValidators();
      this.createSubsidiaryForm.get('ownerEmail').clearValidators();
      this.createSubsidiaryForm.get('subsidiaryId').setValidators([Validators.required]);
    }

    this.cd.detectChanges();
  }
}
